import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
  FinteqHubProcessing,
  ProcessOperationResponse,
} from '@finteqhub/sdk-js';
import { getDeviceData } from '@finteqhub/sdk-js/dist/src/utils';
import i18n from 'i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  FinteqHubPreprocessorParameters,
  InputPreprocessors,
  InputTypes,
  PaymentMethodConfiguration,
  PaymentMethodInput,
  PreprocessorParam,
} from '@cashier/cashier-model';

import { DataConverter } from './dataConverter';

const deviceData = {
  ...getDeviceData().device.browser,
  fingerprint: uuidv4().replaceAll('-', ''),
  type: getDeviceData().device.type,
  javaEnabled: String(getDeviceData().device.browser.javaEnabled),
  javaScriptEnabled: String(getDeviceData().device.browser.javaScriptEnabled),
};

const setupFingerprint = async () => {
  const fp = await FingerprintJS.load();

  return fp.get();
};

const shouldProcessSession = (preprocessor?: PreprocessorParam) => {
  return [
    InputPreprocessors.FINTEQ_HUB_CARD_WITHDRAWAL,
    InputPreprocessors.FINTEQ_HUB_CARD_DEPOSIT,
  ].includes(preprocessor?.type as InputPreprocessors);
};

const setupInstance = (
  parameters: FinteqHubPreprocessorParameters,
  visitorId: string,
  sessionId: string,
) => {
  return new FinteqHubProcessing(
    parameters.preprocessorUrl,
    visitorId,
    parameters.pspMerchantId,
    sessionId,
  );
};

const getCvvField = (): PaymentMethodInput => ({
  name: 'cvv',
  label: i18n.t('cvv.label'),
  required: true,
  type: InputTypes.Cvv,
  placeholder: i18n.t('cvv.placeholder'),
  value: null,
  params: {
    options: [],
    preprocessed: true,
    shouldBeStripped: true,
  },
});

const getFinteqInputs = (): PaymentMethodInput[] => [
  {
    name: 'cardHolder',
    label: i18n.t('cardHolder.label'),
    required: true,
    type: InputTypes.Input,
    placeholder: i18n.t('cardHolder.placeholder'),
    value: null,
    params: {
      options: [],
      preprocessed: false,
      shouldBeStripped: true,
    },
  },
  {
    name: 'creditcardNumber',
    label: i18n.t('creditcardNumber.label'),
    required: true,
    type: InputTypes.CreditCardNumber,
    placeholder: i18n.t('creditcardNumber.placeholder'),
    value: null,
    params: {
      options: [],
      preprocessed: true,
      shouldBeStripped: true,
    },
  },
  getCvvField(),
  {
    name: 'expirationDate',
    label: i18n.t('expirationDate.label'),
    required: true,
    type: InputTypes.DateTime,
    placeholder: i18n.t('expirationDate.placeholder'),
    mask: '99/99',
    value: null,
    params: {
      options: [],
      preprocessed: true,
      shouldBeStripped: true,
    },
  },
];

export const isFinteqHubCardDeposit = (preprocessor?: PreprocessorParam) =>
  preprocessor?.type === InputPreprocessors.FINTEQ_HUB_CARD_DEPOSIT;

export const FinteqHubService = {
  enrichInputsWithCardItems(
    method: PaymentMethodConfiguration,
    showMethodInputs: boolean,
  ): PaymentMethodInput[] {
    const { inputs, account_form_inputs } = method;

    return showMethodInputs
      ? [...inputs, ...getFinteqInputs()]
      : [...account_form_inputs, getCvvField()];
  },
  enrichInputsWithSessionItems(
    preprocessor: PreprocessorParam,
    input: PaymentMethodInput,
  ): PaymentMethodInput {
    if (input.params?.preprocessed && shouldProcessSession(preprocessor)) {
      return {
        ...input,
        value: deviceData[input.name] ?? null,
        isValid: Boolean(deviceData[input.name]),
      };
    }

    return {
      ...input,
      isValid: false,
    };
  },
  async processPayment(
    paymentAttributes: PaymentMethodInput[],
    paymentInputs: Record<string, PaymentMethodInput>,
    preprocessor: PreprocessorParam,
  ): Promise<ProcessOperationResponse> {
    const { visitorId } = await setupFingerprint();
    const { sessionId, externalAccountId } = paymentAttributes.reduce(
      (acc, attr) => {
        if (attr.name === 'sessionId') acc.sessionId = attr.value;
        if (attr.name === 'externalAccountId')
          acc.externalAccountId = attr.value;

        return acc;
      },
      { sessionId: undefined, externalAccountId: undefined },
    );

    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    const processing = setupInstance(
      preprocessor.parameters as FinteqHubPreprocessorParameters,
      visitorId,
      sessionId,
    );

    const finteqHubSession = await processing.getSession();
    const payload = DataConverter.toFormPayload(
      finteqHubSession,
      paymentInputs,
      externalAccountId,
    );

    return await processing.submitForm(payload);
  },
};
